import type { Spot } from '../ScopingEditor/useGetState';
import { type Dt3pDeviceStatus, DT3P_STATUS_COLORS } from '../ScopingEditor/dt3pStatus';
import {
  type LiveOccupancyStatus,
  LIVE_OCC_COLORS,
  SPOT_LIVE_FILTER_ID,
} from '../ScopingEditor/liveOccupancy';
import { SVGProps, memo } from 'react';
import { AutoTooltip } from '@/components';
import { ICONS_SPRITE_URL } from '@/components/primitives/icon/Icon';
import { useZoom } from '../ZoomContext';
import { useCanvasColors } from './canvasColors';

export const SPOT_ICON_SIZE = 1.75;

// Fixed IDs for the single shared set of spot filters.
// All SpotItem instances reference these instead of creating per-instance <defs>.
const SPOT_FILTER_NORMAL_ID = 'spot-filter-normal';
const SPOT_FILTER_SELECTED_ID = 'spot-filter-selected';
const SPOT_FILTER_STATUS_PLACEHOLDER_ID = 'spot-filter-status-placeholder';
const SPOT_FILTER_STATUS_WARNING_ID = 'spot-filter-status-warning';
const SPOT_FILTER_STATUS_CONNECTED_ID = 'spot-filter-status-connected';
const SPOT_FILTER_STATUS_ERROR_ID = 'spot-filter-status-error';

const OCCUPIED_STATUS_FILTER_ID: Record<Dt3pDeviceStatus, string> = {
  placeholder: SPOT_FILTER_STATUS_PLACEHOLDER_ID,
  warning: SPOT_FILTER_STATUS_WARNING_ID,
  connected: SPOT_FILTER_STATUS_CONNECTED_ID,
  error: SPOT_FILTER_STATUS_ERROR_ID,
};

/**
 * Renders a single shared <defs> block with the spot color filters.
 * Mount this once inside the parent SVG so all SpotItem instances can
 * reference the filters by ID without duplicating them.
 */
export function SpotFilters() {
  const colors = useCanvasColors();
  return (
    <defs>
      {/* Normal: recolor icon to zone idle gray */}
      <filter id={SPOT_FILTER_NORMAL_ID}>
        <feFlood floodColor={colors.roomStrokeIdle} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Selected: recolor icon using theme-aware stroke color */}
      <filter id={SPOT_FILTER_SELECTED_ID}>
        <feFlood floodColor={colors.roomStroke} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Occupied status filters — color the spot to match the snapped sensor3p's status */}
      <filter id={SPOT_FILTER_STATUS_PLACEHOLDER_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.placeholder} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      <filter id={SPOT_FILTER_STATUS_WARNING_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.warning} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      <filter id={SPOT_FILTER_STATUS_CONNECTED_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.connected} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      <filter id={SPOT_FILTER_STATUS_ERROR_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.error} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Live occupancy filters — color the spot by desk occupancy in the live floor plan view */}
      <filter id={SPOT_LIVE_FILTER_ID.available}>
        <feFlood floodColor={LIVE_OCC_COLORS.available} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      <filter id={SPOT_LIVE_FILTER_ID.occupied}>
        <feFlood floodColor={LIVE_OCC_COLORS.occupied} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      <filter id={SPOT_LIVE_FILTER_ID.unknown}>
        <feFlood floodColor={LIVE_OCC_COLORS.unknown} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
    </defs>
  );
}

function SpotItem({
  x = 0,
  y = 0,
  name,
  id,
  selected,
  selecting = false,
  fade = false,
  locked = false,
  isOccupied = false,
  occupiedStatus,
  liveOccupiedStatus,
  historicalColor,
  status,
  onClick,
  ...rest
}: {
  selected: boolean;
  selecting?: boolean;
  fade?: boolean;
  locked?: boolean;
  isOccupied?: boolean;
  /** Status of the sensor3p snapped to this spot — drives the spot's fill color. */
  occupiedStatus?: Dt3pDeviceStatus;
  /** Live desk occupancy status — when set, overrides `occupiedStatus` for color. Used in the live floor plan view. */
  liveOccupiedStatus?: LiveOccupancyStatus;
  /** Historical utilization color — when set, renders a gradient background behind the icon. Used in the historical floor plan view. */
  historicalColor?: string;
  /** Tooltip status row — mirrors the room/zone ItemRenderer pattern. */
  status?: { label: string; value: string };
  id: string;
} & SVGProps<SVGGElement> &
  Pick<Spot, 'name'> & { x?: number; y?: number }) {
  const { adjustedZoom } = useZoom();
  const size = SPOT_ICON_SIZE / adjustedZoom;
  const showAsSelected = selected || selecting;
  const filterId = showAsSelected
    ? SPOT_FILTER_SELECTED_ID
    : historicalColor
    ? SPOT_FILTER_NORMAL_ID
    : isOccupied && liveOccupiedStatus
    ? SPOT_LIVE_FILTER_ID[liveOccupiedStatus]
    : isOccupied && occupiedStatus
    ? OCCUPIED_STATUS_FILTER_ID[occupiedStatus]
    : SPOT_FILTER_NORMAL_ID;

  return (
    <g
      data-id="spot-item"
      id={id}
      onClick={onClick}
      transform={`translate(${x}, ${y})`}
      style={{ pointerEvents: locked ? 'none' : 'all', opacity: fade ? 0.5 : 1 }}
      data-selected={selected}
      {...rest}
    >
      <AutoTooltip
        zIndex={100}
        label={
          <div className="flex flex-col gap-2">
            <h3 className="font-semibold">{name}</h3>
            {status && (
              <div className="flex flex-row justify-between">
                <span className="mr-2">{status.label}</span>
                <span>{status.value}</span>
              </div>
            )}
          </div>
        }
        variant="white"
        placement="top"
        keepOpenOnMouseOver
        forceOpen={selected}
        autoUpdateOnAnimationFrame
      >
        {(props) => (
          <g transform="scale(1, -1)" {...props}>
            {historicalColor && (
              /* Historical: inline filter colors the filled icon per-spot without shared filter IDs */
              <>
                <defs>
                  <filter id={`spot-hist-${id}`}>
                    <feFlood floodColor={historicalColor} result="color" />
                    <feComposite in="color" in2="SourceAlpha" operator="in" />
                  </filter>
                </defs>
                <use
                  href={`${ICONS_SPRITE_URL}#icon-icon-spotfilled`}
                  width={size}
                  height={size}
                  x={-size / 2}
                  y={-size / 2}
                  filter={`url(#spot-hist-${id})`}
                  pointerEvents="none"
                />
              </>
            )}
            {isOccupied && !historicalColor && (
              /* Filled variant of the same icon shape — sensor3p renders on top */
              <use
                href={`${ICONS_SPRITE_URL}#icon-icon-spotfilled`}
                width={size}
                height={size}
                x={-size / 2}
                y={-size / 2}
                filter={`url(#${filterId})`}
                pointerEvents="none"
              />
            )}
            {/* pointer-events:none so the <use> bounding box doesn't expand the hit area */}
            <use
              href={`${ICONS_SPRITE_URL}#icon-icon-spotoutlined`}
              width={size}
              height={size}
              x={-size / 2}
              y={-size / 2}
              filter={`url(#${filterId})`}
              pointerEvents="none"
            />
            {/* sole hit target: circle sized to match the visible icon */}
            <circle cx={0} cy={0} r={size * 0.4} fill="transparent" pointerEvents="all" />
          </g>
        )}
      </AutoTooltip>
    </g>
  );
}

export default memo(SpotItem);
